import { useState } from "react";
import { Card } from "@/components/ui/card";
import TaskAnalysis from "@/components/TaskAnalysis";
import NextFocus from "@/components/NextFocus";
import { SkillsRadarChart } from "@/components/SkillsRadarChart";
import { Button } from "@/components/ui/button";
import ConfirmationModal from "@/components/ui/ConfirmationModal";

const skillData = [
    { skill: "Grammar", score: 70 },
    { skill: "Vocabulary", score: 85 },
    { skill: "Comprehension", score: 78 },
    { skill: "Fluency", score: 75 },
    { skill: "Pronunciation", score: 80 },
];

const isGuest = false;

const displayData = {
  tasks: [
    {
      index: 1,
      cefr_level: 'B2',
      title: 'Task 1 — Self introduction - Level B2',
      transcript_snippets: ["« Je m'appelle Léandre et je vis à Vancouver depuis trois ans... »", "« Dans mon temps libre, j'aime aller au gym et voyager avec mes amis... »"],
      improved_version: {
        show: true,
        text_fr: "« Je m'appelle Léandre et j'habite à Vancouver depuis trois ans. Pendant mon temps libre, je vais régulièrement au gymnase et j'aime voyager, car cela me permet de découvrir de nouvelles cultures et de rencontrer des personnes différentes. »"
      },
      native_example: {
        available: true,
        text_fr: "« Bonjour, je m'appelle Sophie, je viens de Lyon et j'habite à Montréal depuis deux ans. J'ai étudié l'architecture à l'université et je travaille maintenant dans un bureau de design. Pendant mon temps libre, j'adore faire de la randonnée dans les montagnes, cuisiner pour mes amis et découvrir de nouvelles musiques. J'aime aussi voyager, parce que cela me donne l'occasion de pratiquer les langues étrangères et d'élargir mes horizons culturels. À l'avenir, j'aimerais continuer à développer ma carrière au Canada tout en gardant un lien fort avec ma famille en France. »"
      },
      micro_tips: [
        {
          type: 'vocabulary',
          label_en: 'Use "j\'habite" instead of "je vis"',
          details_en: 'Say "j\'habite à Vancouver" instead of "je vis" — it sounds more natural.'
        },
        {
          type: 'connectors',
          label_en: 'Add connectors',
          details_en: 'Add connectors like "car" and "à l\'avenir" to link ideas smoothly.'
        }
      ]
    },
    {
      index: 2,
      cefr_level: 'A2',
      title: 'Task 2 — Role play Q&A - Level A2',
      transcript_snippets: [],
      improved_version: { show: false, text_fr: '' },
      native_example: { available: false, text_fr: '' },
      micro_tips: []
    },
    {
      index: 3,
      cefr_level: 'B1',
      title: 'Task 3 — Opinion discussion - Level B1',
      transcript_snippets: [],
      improved_version: { show: false, text_fr: '' },
      native_example: { available: false, text_fr: '' },
      micro_tips: []
    }
  ]
};

const Feedback = () => {

      // ⭐ Review modal state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isReviewLoading, setIsReviewLoading] = useState(false);

  // ⭐ Google Review link (replace with your real URL later)
        const GOOGLE_REVIEW_URL =
        "https://g.page/r/YOUR_GOOGLE_PLACE_ID/review";
      
    // ⭐ When user clicks "Leave a Google review"
          const handleConfirmReview = () => {
            try {
              setIsReviewLoading(true);
        
              // Mark as reviewed so we never ask again later
              localStorage.setItem("p2p_has_left_review", "true");
        
              // Open Google reviews in a new tab
              window.open(GOOGLE_REVIEW_URL, "_blank", "noopener,noreferrer");
            } catch (err) {
              console.error("Error opening Google review link:", err);
            } finally {
              setIsReviewLoading(false);
              setShowReviewModal(false);
            }
          };
        
          // ⭐ When user clicks "Not now"
          const handleCancelReview = () => {
            // For now, just close the modal.
            // (We’ll add the 30-day snooze later.)
            setShowReviewModal(false);
          };

    
    return (
        // ⭐ Same layout wrapper used across the entire SaaS app
    <div className="w-full flex flex-col items-center px-4 bg-background">
      {/* ⭐ EXACT SAME max width as DataTable: 1216px */}
      <main className="w-full max-w-[1216px] pt-24 pb-10 px-6">
                {/* Results Badge */}
                <div className="text-center mb-12">
                    <h1 className="text-2xl font-semibold text-foreground mb-4">
                        Your TCF Speaking Results
                    </h1>
                    {/* <p className="text-muted-foreground text-sm mb-8">March 16, 2024</p> */}

                    <div className="inline-flex items-center justify-center w-32 h-32 rounded-full mb-6 bg-gradient-to-r from-blue-500 to-sky-400">
                        <span className="text-white text-4xl font-bold">B2</span>
                    </div>


                    <p className="text-muted-foreground">
                        You've achieved your goal, amazing work.
                    </p>
                </div>

                {/* Skills Radar Chart */}
                    <div className="rounded-lg p-8 mb-8">
                      <h2 className="text-xl font-semibold text-center mb-6 text-foreground">
                        Your Skill Breakdown
                      </h2>
                    
                      <div className="w-full max-w-[600px] mx-auto">
                        <SkillsRadarChart data={skillData} />
                      </div>
                    
                      <div className="text-center mt-4">
                        <p className="text-sm text-muted-foreground mb-2">
                          You spoke smoothly and with confidence.
                        </p>
                        <p className="text-xs text-muted-foreground">
                          To reach the next level, work on building stronger sentences and adding richer vocabulary.
                        </p>
                      </div>
                    </div>

                <div className="mb-8">
                    <h2 className="text-xl font-semibold text-center mb-2 text-gray-800">
                        Detailed Task Analysis
                    </h2>
                    <p className="text-gray-500 text-center text-sm mb-6">
                        Review your responses and see how to improve
                    </p>
                    {/* <TaskAnalysis /> */}
                </div>


                {/* <div className="mb-8">
                    <h2 className="text-xl font-semibold text-center mb-6 text-gray-800">
                        Your Next Focus
                    </h2>
                    <NextFocus />
                </div> */}

                {/* Action Buttons */}
                <div className="flex flex-col items-center gap-4">
                    <Button className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-lg font-semibold">
                        Push Toward C1
                    </Button>

                     {/* 🔧 TEMP: test button to open the review modal */}
                    <button
                      type="button"
                      onClick={() => setShowReviewModal(true)}
                      className="text-xs text-muted-foreground hover:underline"
                    >
                      (Test review modal)
                    </button>

                    <div className="flex gap-4 text-sm">
                        <button className="text-primary hover:underline">
                            Repeat
                        </button>
                        <button className="text-primary hover:underline">
                            Select a task
                        </button>
                    </div>

                    <button className="self-start text-muted-foreground text-sm hover:underline mt-4">
                        ← Back to Dashboard
                    </button>

                </div>
                  {/* ⭐ Review request modal */}
                  <ConfirmationModal
                  open={showReviewModal}
                  onOpenChange={setShowReviewModal}
                  title="Happy with your TCF prep so far?"
                  description="Your feedback helps other students feel confident choosing Prep2Pass. If we’ve made your TCF Canada prep easier, would you mind leaving a quick Google review?"
                  confirmLabel="Leave a Google review"
                  cancelLabel="Not now"
                  onConfirm={handleConfirmReview}
                  onCancel={handleCancelReview}
                  isLoading={isReviewLoading}
                />
            </main>
        </div>
    );
};
export default Feedback;
